import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {
  ClothingCatalogItemList,
  ClothingOption,
  ClothingProcurementCalendarDiagnostic,
  ClothingProcurementForecast,
  ClothingProcurementForecastFilters
} from '../../../models/clothing.model';
import { ClothingErrorService } from '../../../services/clothing-error.service';
import { ClothingService } from '../../../services/clothing.service';
import {
  CLOTHING_PROCUREMENT_DEFAULTS,
  CLOTHING_PROCUREMENT_VALIDATION_KEYS
} from './clothing.constants';
import {
  ClothingProcurementForecastMapper
} from './clothing-procurement-forecast.mapper';
import {
  ClothingProcurementDepartmentView,
  ClothingProcurementForecastViewRow,
  ClothingProcurementSizeBar
} from './clothing-procurement-forecast.option';

@Component({
  selector: 'app-clothing-procurement-forecast',
  templateUrl: './clothing-procurement-forecast.component.html',
  styleUrls: ['./clothing-procurement-forecast.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClothingProcurementForecastComponent implements OnInit, OnDestroy {
  @Input() public departments: ClothingOption[] = [];
  @Input() public catalogItems: ClothingCatalogItemList[] = [];

  public filters: ClothingProcurementForecastFilters = this.createFilters();
  public selectedGroupIds: string[] = [];
  public forecastDepartments: ClothingProcurementDepartmentView[] = [];
  public expandedGroupId = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
  public forecast: ClothingProcurementForecast;
  public errorMessage = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
  public validationErrorKey = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
  public isLoading = false;
  public hasForecast = false;

  private readonly destroy$ = new Subject<void>();
  private readonly forecastMapper = new ClothingProcurementForecastMapper();

  public constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private clothingErrorService: ClothingErrorService,
    private clothingService: ClothingService
  ) {}

  public ngOnInit(): void {
    this.selectAllDepartments();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isGenerateDisabled(): boolean {
    return this.isLoading
      || this.isArrivalDateMissing()
      || this.areDepartmentsMissing();
  }

  public selectAllDepartments(): void {
    this.selectedGroupIds = this.departments.map((department) => department.id);
    this.validationErrorKey = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
  }

  public clearDepartments(): void {
    this.selectedGroupIds = [];
  }

  public isDepartmentSelected(groupId: string): boolean {
    return this.selectedGroupIds.indexOf(groupId) !== CLOTHING_PROCUREMENT_DEFAULTS.missingIndex;
  }

  public toggleDepartment(groupId: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked && !this.isDepartmentSelected(groupId)) {
      this.selectedGroupIds = this.selectedGroupIds.concat(groupId);
    }
    if (!checkbox.checked) {
      this.selectedGroupIds = this.selectedGroupIds.filter((id) => id !== groupId);
    }
  }

  public toggleForecastDepartment(groupId: string): void {
    this.expandedGroupId = this.expandedGroupId === groupId
      ? CLOTHING_PROCUREMENT_DEFAULTS.emptyValue
      : groupId;
  }

  public loadForecast(): void {
    if (!this.validateFilters()) {
      return;
    }
    this.isLoading = true;
    this.clearErrors();
    this.clothingService.getProcurementForecast(this.createRequest())
      .takeUntil(this.destroy$)
      .subscribe(
        (forecast) => this.handleForecastLoaded(forecast),
        (error: HttpErrorResponse) => this.handleRequestFailed(error)
      );
  }

  public trackByDepartment(index: number, department: ClothingOption): string {
    return department.id;
  }

  public trackByCatalogItem(index: number, item: ClothingCatalogItemList): string {
    return item.id;
  }

  public trackByCalendarDiagnostic(index: number, diagnostic: ClothingProcurementCalendarDiagnostic): string {
    return diagnostic.markerId;
  }

  public trackByRow(index: number, row: ClothingProcurementForecastViewRow): string {
    return row.key;
  }

  public trackByForecastDepartment(index: number, department: ClothingProcurementDepartmentView): string {
    return department.groupId;
  }

  public trackBySize(index: number, size: ClothingProcurementSizeBar): string {
    return String(size.size);
  }

  private validateFilters(): boolean {
    if (this.isArrivalDateMissing()) {
      this.validationErrorKey = CLOTHING_PROCUREMENT_VALIDATION_KEYS.arrivalDateRequired;
      return false;
    }
    if (this.areDepartmentsMissing()) {
      this.validationErrorKey = CLOTHING_PROCUREMENT_VALIDATION_KEYS.departmentsRequired;
      return false;
    }
    return true;
  }

  private isArrivalDateMissing(): boolean {
    return !this.filters.expectedArrivalDate.length;
  }

  private areDepartmentsMissing(): boolean {
    return !this.selectedGroupIds.length;
  }

  private createRequest(): ClothingProcurementForecastFilters {
    return Object.assign({}, this.filters, {
      groupIds: this.selectedGroupIds.slice(),
      leadTimeDays: CLOTHING_PROCUREMENT_DEFAULTS.leadTimeDays,
      shortageOnly: CLOTHING_PROCUREMENT_DEFAULTS.shortageOnly
    });
  }

  private handleForecastLoaded(forecast: ClothingProcurementForecast): void {
    this.forecast = forecast;
    this.forecastDepartments = this.forecastMapper.map(forecast);
    this.expandedGroupId = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
    this.hasForecast = true;
    this.isLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  private handleRequestFailed(error: HttpErrorResponse): void {
    this.errorMessage = this.clothingErrorService.resolveDefaultMessage(error);
    this.isLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  private clearErrors(): void {
    this.errorMessage = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
    this.validationErrorKey = CLOTHING_PROCUREMENT_DEFAULTS.emptyValue;
  }

  private createFilters(): ClothingProcurementForecastFilters {
    return {
      expectedArrivalDate: CLOTHING_PROCUREMENT_DEFAULTS.emptyValue,
      leadTimeDays: CLOTHING_PROCUREMENT_DEFAULTS.leadTimeDays,
      safetyBufferPercent: CLOTHING_PROCUREMENT_DEFAULTS.safetyBufferPercent,
      groupIds: [],
      catalogItemId: CLOTHING_PROCUREMENT_DEFAULTS.emptyValue,
      shortageOnly: CLOTHING_PROCUREMENT_DEFAULTS.shortageOnly
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-procurement-forecast.component.ts